import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { fetchConfigurationMetadata } from '../../services/api';
import { Box, Typography, MenuItem, Radio, RadioGroup, FormControlLabel, Button, InputAdornment, Collapse, Tooltip, Divider } from '@mui/material';
import { ChevronLeft as LeftIcon, ChevronRight as RightIcon, Search as SearchIcon, Print as PrintIcon, GetApp as DownloadIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, CalendarToday as CalendarIcon, Close as CloseIcon } from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { COLORS, PageWrapper, SidebarContainer, Breadcrumb, SectionHeader, FilterLabel, StyledInput, StyledSelect, RadioLabelText, MainContentArea, ContentCard, CardHeader, UserSummarySection, UserInfoWrapper, UserAvatar, StatCarouselWrapper, StatGroup, StatItem, StatScrollButton, NavSidebar, NavIconItem, VerticalTimelineAxis, TimelineItemContainer, TimelineLeftContent, TimelineDot, TimelineHorizontalConnector, TimelineSummaryChip, TimelineSummaryText } from './AuditTrails.styles';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import health_plan from '../../assets/icons/left_side_menu/sub_menu/Deselected/SelectPlans_Icons_deselected.svg';
import filter from '../../assets/icons/left_side_menu/sub_menu/Deselected/Filter_Deselected.svg';
import filteredIcon from '../../assets/icons/left_side_menu/sub_menu/Selected/filter_Selected.svg';
import manage_clear_provider from '../../assets/icons/left_side_menu/sub_menu/clearfilter_icon_flat.svg';
import views from '../../assets/icons/Views.svg';
import manage_provider_add from '../../assets/icons/left_side_menu/sub_menu/Deselected/AddProvider.svg';
import manage_delete_provider from '../../assets/icons/left_side_menu/sub_menu/Deselected/DeleteProvider.svg';
import { useDispatch, useSelector } from 'react-redux';
import { getFilterData } from '../../redux/reducers/myProviderSlice';
import HealthPlanSelector from './HealthPlanSelector'; // Imported Health Plan component
import { fetchFilter } from '../../services/api';
import { fetchChangeHistory } from '../../services/api/auditTrail';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import AuditTimeline from './AuditTimeline';
import './AuditTrailsFix.css';

const slotPropsForToolTips = {
    popper: {
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: [0, -10],
                },
            },
        ],
    },
};

const formatDate = (date) => {
    if (!date) return null;
    const d = new Date(date);
    if (isNaN(d.getTime())) return null;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const resolveDateRange = (dateFilter, fromDate, toDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (dateFilter === 'custom') {
        if (!fromDate || !toDate) return { resolvedFrom: null, resolvedTo: null };
        const from = new Date(fromDate);
        const to = new Date(toDate);
        from.setHours(0, 0, 0, 0);
        to.setHours(0, 0, 0, 0);
        if (from > to) return { resolvedFrom: null, resolvedTo: null };
        return { resolvedFrom: formatDate(from), resolvedTo: formatDate(to) };
    }

    const presets = {
        last6months: 180,
        last3months: 90,
        lastmonth: 30,
        lastweek: 7
    };

    const days = presets[dateFilter];
    if (!days) return { resolvedFrom: null, resolvedTo: null };

    const from = new Date(today);
    from.setDate(from.getDate() - days);
    return { resolvedFrom: formatDate(from), resolvedTo: formatDate(today) };
};

const AuditTrails = () => {
    const dispatch = useDispatch();
    const responseData = useSelector((state) => state.myProvider.responseData);
    const [healthPlans, setHealthPlans] = useState([]);
    const [selectedHealthPlanIds, setSelectedHealthPlanIds] = useState([]);
    const [searchProvider, setSearchProvider] = useState('');
    const [selectedSection, setSelectedSection] = useState('');
    const [selectedField, setSelectedField] = useState('');
    const [searchByNpi, setSearchByNpi] = useState('');
    const [metaData, setMetaData] = useState([]);
    const [auditHistoryData, setAuditHistoryData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [gridApi, setGridApi] = useState(null);
    const [pageNo, setPageNo] = useState(1);
    const [columnDefs, setColumnDefs] = useState([]);
    const [displayedRowCount, setDisplayedRowCount] = useState(0);

    const [activeView, setActiveView] = useState(null);
    const sidebarOpen = activeView !== null;

    const [dateFilter, setDateFilter] = useState('');
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [filterSearchValue, setFilterSearchValue] = useState('');
    const [totalRecords, setTotalRecords] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [paginationPageSize] = useState(20);
    const hasGeneratedColumns = React.useRef(false);

    const filtersRef = React.useRef({
        selectedHealthPlanIds: [],
        searchProvider: '',
        selectedField: '',
        filterSearchValue: '',
        searchByNpi: '',
        dateFilter: '',
        fromDate: null,
        toDate: null
    });

    useEffect(() => {
        filtersRef.current = {
            selectedHealthPlanIds,
            searchProvider,
            selectedField,
            filterSearchValue,
            searchByNpi,
            dateFilter,
            fromDate,
            toDate
        };
    }, [selectedHealthPlanIds, searchProvider, selectedField, filterSearchValue, searchByNpi, dateFilter, fromDate, toDate]);

    const lastApiStateRef = React.useRef({
        filterString: '',
        rows: [],
        total: 0
    });

    useEffect(() => {
        const timer = setTimeout(() => {
            if (gridApi) {
                gridApi.refreshServerSide({ purge: false });
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [searchProvider, gridApi]);

    useEffect(() => {
        if (healthPlans.length === 0) {
            const loadHealthPlans = async () => {
                try {
                    const response = await fetchFilter();
                    const plans = response?.response_data?.health_plans?.filter_columns?.[0]?.filter_data || [];
                    setHealthPlans(plans);
                } catch (error) {
                    console.error("Failed to fetch health plans:", error);
                }
            };
            loadHealthPlans();
        }
    }, []);

    useEffect(() => {
        if (gridApi) {
            if (loading) {
                gridApi.hideOverlay();
            } else if (totalRecords === 0) {
                gridApi.showNoRowsOverlay();
            } else {
                gridApi.hideOverlay();
            }
        }
    }, [loading, totalRecords, gridApi]);

    const defaultColDef = useMemo(() => ({
        resizable: true,
        sortable: false,
        filter: true,
        flex: 1,
        minWidth: 100,
        suppressMenu: true
    }), []);

    useEffect(() => {
        if (auditHistoryData && auditHistoryData.length > 0) {
            const uniqueLabels = [];
            auditHistoryData.forEach(item => {
                if (item.summary.stats) {
                    item.summary.stats.forEach(s => {
                        if (!uniqueLabels.includes(s.label)) {
                            uniqueLabels.push(s.label);
                        }
                    });
                }
            });

            if (uniqueLabels.length === 0 && columnDefs.length > 0) return;
            if (hasGeneratedColumns.current && columnDefs.length > 1) return;

            const newDefs = [
                {
                    headerName: 'Provider Details',
                    field: 'providerName',
                    pinned: 'left',
                    sortable: true,
                    minWidth: 280,
                    cellRenderer: 'agGroupCellRenderer',
                    cellRendererParams: {
                        innerRenderer: (params) => {
                            const summary = params.data.summary;
                            if (!summary) return null;
                            const initials = summary.initials || summary.name?.split(' ').map(n => n[0]).join('').substring(0, 2) || 'NA';
                            return (
                                <UserInfoWrapper sx={{ height: '100%', alignItems: 'center', justifyContent: 'flex-start', gap: '10px' }}>
                                    <UserAvatar sx={{ width: '28px', height: '28px', fontSize: '10px', m: 0 }}>{initials}</UserAvatar>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#344049', lineHeight: 1.1, textAlign: 'left' }}>{summary.name}</Typography>
                                        <Typography sx={{ fontSize: '11px', color: '#757575', lineHeight: 1.1, textAlign: 'left' }}>NPI: {summary.npi}</Typography>
                                    </Box>
                                </UserInfoWrapper>
                            );
                        }
                    },
                    suppressMovable: true,
                    headerClass: 'ag-header-cell-left'
                },
                ...uniqueLabels.map((label) => ({
                    headerName: label,
                    valueGetter: (params) => {
                        const stat = params.data.summary.stats?.find(item => item.label === label);
                        return (stat && stat.value !== undefined) ? stat.value : '-';
                    },
                    width: 100,
                    headerClass: 'ag-header-cell-center',
                    cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'normal', fontSize: '12px', textAlign: 'center' }
                }))
            ];

            const currentLabels = columnDefs.filter(d => !d.pinned).map(d => d.headerName);
            if (JSON.stringify(currentLabels) !== JSON.stringify(uniqueLabels)) {
                setColumnDefs(newDefs);
                hasGeneratedColumns.current = true;
            }
        }
    }, [auditHistoryData, columnDefs]);

    const handleToggle = async (view) => {
        if (activeView === view) {
            setActiveView(null);
        } else {
            setActiveView(view);
            if (view === 'filter') {
                if (metaData && metaData.length > 0) return;
                try {
                    const res = await fetchConfigurationMetadata();
                    if (res.is_successful) {
                        setMetaData(res.response_data.MetaData || []);
                    }
                } catch (err) {
                    setMetaData([]);
                }
            }
        }
    };

    const handleNpiChange = (e) => {
        const val = e.target.value.replace(/\D/g, '');
        setSearchByNpi(val);

    };

    const handleHealthPlanApply = (selectedIds) => {
        setSelectedHealthPlanIds(selectedIds);
        filtersRef.current.selectedHealthPlanIds = selectedIds;
        if (gridApi) {
            gridApi.refreshServerSide({ purge: true });
        }
    };

    const callChangeHistoryAPI = async (overrideHealthPlanIds = null, overrideFilterSearchValue = null, page = 1, currentPageSize = 20, shouldSetState = true, sortColumn = "", sortOrder = "") => {
        try {
            if (shouldSetState) setLoading(true);
            const currentFilters = filtersRef.current;
            const healthPlanIds = overrideHealthPlanIds !== null ? overrideHealthPlanIds : currentFilters.selectedHealthPlanIds;
            const searchValue = overrideFilterSearchValue !== null ? overrideFilterSearchValue : currentFilters.filterSearchValue;
            const { searchByNpi, searchProvider, selectedField, dateFilter, fromDate, toDate } = currentFilters;

            const searchFields = [];
            if (healthPlanIds && healthPlanIds.length > 0) {
                searchFields.push({
                    search_key: "healthPlanId",
                    search_value: healthPlanIds.join(',')
                });
            }

            if (searchByNpi) {
                searchFields.push({
                    search_key: "npi",
                    search_value: searchByNpi
                });
            }

            if (selectedField && searchValue) {
                searchFields.push({
                    search_key: selectedField,
                    search_value: searchValue
                });
            }

            const { resolvedFrom, resolvedTo } = resolveDateRange(dateFilter, fromDate, toDate);

            const payload = {
                search_fields: searchFields,
                sort_column: sortColumn || "",
                sort_des: sortOrder || "",
                view_id: 0,
                total_row: currentPageSize,
                page_no: page,
                ...(resolvedFrom && resolvedTo && {
                    fromDate: resolvedFrom,
                    toDate: resolvedTo
                })
            };

            console.debug('[AuditTrails] dateFilter:', dateFilter);
            console.debug('[AuditTrails] fromDate:', resolvedFrom);
            console.debug('[AuditTrails] toDate:', resolvedTo);
            console.debug('[AuditTrails] final payload:', JSON.stringify(payload, null, 2));

            const response = await fetchChangeHistory(payload);

            if (response?.is_successful) {
                const rows = response?.response_data?.rows || [];
                const recordInfo = response?.response_data?.recordInfo || response?.response_data?.record_info;
                const total = recordInfo?.total_records || response?.response_data?.total_rows || rows.length;

                if (shouldSetState) {
                    setAuditHistoryData(rows);
                    setTotalRecords(total);
                    setPageNo(page);
                    setErrorMessage('');
                }
                return { rows, total };
            } else {
                if (shouldSetState) {
                    setAuditHistoryData([]);
                    setTotalRecords(0);
                    setErrorMessage(response?.user_message || 'Failed to fetch data');
                }
                return { rows: [], total: 0 };
            }
        } catch (error) {
            return { rows: [], total: 0 };
        } finally {
            if (shouldSetState) setLoading(false);
        }
    };

    const handleViewAudits = () => {
        setPageNo(1);
        filtersRef.current = {
            ...filtersRef.current,
            searchByNpi,
            selectedField,
            filterSearchValue,
            dateFilter,
            fromDate,
            toDate
        };
        if (gridApi) {
            gridApi.refreshServerSide({ purge: true });
        } else {
            callChangeHistoryAPI(null, null, 1);
        }
        setActiveView(null);
    };

    const handleResetFilters = () => {
        setSearchProvider('');
        setFilterSearchValue('');
        setSelectedSection('');
        setSelectedField('');
        setSearchByNpi('');
        setDateFilter('');
        setFromDate(null);
        setToDate(null);
        setPageNo(1);

        filtersRef.current = {
            selectedHealthPlanIds: filtersRef.current.selectedHealthPlanIds,
            searchProvider: '',
            filterSearchValue: '',
            selectedField: '',
            searchByNpi: '',
            dateFilter: '',
            fromDate: null,
            toDate: null
        };

        if (gridApi) {
            gridApi.refreshServerSide({ purge: true });
        }
    };

    const onGridReady = (params) => {
        setGridApi(params.api);

        const datasource = {
            getRows: async (rowParams) => {
                const pageNumber = rowParams.api.paginationGetCurrentPage() + 1;
                const pageSize = rowParams.api.paginationGetPageSize();
                const {
                    selectedHealthPlanIds: currentHP,
                    searchByNpi: currentNPI,
                    selectedField: currentField,
                    filterSearchValue: currentSearch,
                    dateFilter: currentDateMode,
                    fromDate: currentFrom,
                    toDate: currentTo
                } = filtersRef.current;

                const apiFilterState = JSON.stringify({
                    selectedHealthPlanIds: currentHP,
                    searchByNpi: currentNPI,
                    selectedField: currentField,
                    filterSearchValue: currentSearch,
                    dateFilter: currentDateMode,
                    fromDate: currentFrom,
                    toDate: currentTo,
                    pageNumber,
                    pageSize,
                    sortColumn: rowParams.request.sortModel?.[0]?.colId || "",
                    sortOrder: rowParams.request.sortModel?.[0]?.sort || ""
                });

                let result;
                if (lastApiStateRef.current.filterString === apiFilterState) {
                    result = {
                        rows: lastApiStateRef.current.rows,
                        total: lastApiStateRef.current.total
                    };
                } else {
                    setLoading(true);
                    const sortModel = rowParams.request.sortModel?.[0];
                    result = await callChangeHistoryAPI(null, null, pageNumber, pageSize, false, sortModel?.colId, sortModel?.sort);
                    if (result) {
                        lastApiStateRef.current = {
                            filterString: apiFilterState,
                            rows: result.rows,
                            total: result.total
                        };
                    }
                }

                if (result) {
                    if (!hasGeneratedColumns.current && result.rows.length > 0) {
                        setAuditHistoryData(result.rows);
                    }

                    let displayRows = result.rows;
                    const searchTerm = filtersRef.current.searchProvider;
                    if (searchTerm) {
                        const s = searchTerm.toLowerCase();
                        displayRows = result.rows.filter(r =>
                            r.summary.name?.toLowerCase().includes(s) ||
                            r.summary.npi?.toString().includes(s)
                        );
                    }

                    setTotalRecords(searchTerm ? displayRows.length : result.total);

                    rowParams.success({
                        rowData: displayRows,
                        rowCount: searchTerm ? displayRows.length : result.total
                    });
                } else {
                    rowParams.fail();
                }
                setLoading(false);
            }
        };

        params.api.setGridOption('serverSideDatasource', datasource);
        params.api.sizeColumnsToFit();
    };

    const onGridSizeChanged = (params) => {
        params.api.sizeColumnsToFit();
    };

    const onModelUpdated = (params) => {
        params.api.sizeColumnsToFit();
        if (loading) {
            params.api.hideOverlay();
            return;
        }
        if (totalRecords === 0) {
            params.api.showNoRowsOverlay();
        } else {
            params.api.hideOverlay();
        }
    };

    const handleNextPage = () => {
        callChangeHistoryAPI(null, null, pageNo + 1);
    };

    const handlePrevPage = () => {
        if (pageNo > 1) {
            callChangeHistoryAPI(null, null, pageNo - 1);
        }
    };

    const DetailCellRenderer = ({ data }) => {
        return <AuditTimeline data={data.changeHistory} />;
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} >
            <PageWrapper className="audit-trail-no-scroll">
                <NavSidebar>
                    <NavIconItem active={activeView === 'health-plan'} onClick={() => handleToggle('health-plan')} sx={{ position: 'relative' }}>
                        <Box component="img" src={health_plan} sx={{ width: '18px', height: '18px' }} />
                        {selectedHealthPlanIds.length > 0 && (
                            <Box sx={{
                                position: 'absolute', top: '10px', right: '10px', width: '6px', height: '6px',
                                bgcolor: COLORS.primary, borderRadius: '50%', border: '1px solid #F1F1F1'
                            }} />
                        )}
                    </NavIconItem>
                    <Tooltip placement="right" slotProps={slotPropsForToolTips} title="Filter">
                        <NavIconItem active={activeView === 'filter'} onClick={() => handleToggle('filter')} sx={{ position: 'relative' }}>
                            <Box component="img" src={filter} sx={{ width: '18px', height: '18px' }} />
                            {(selectedSection || selectedField || dateFilter || fromDate || toDate || filterSearchValue || searchByNpi) && (
                                <Box sx={{
                                    position: 'absolute', top: '10px', right: '10px', width: '6px', height: '6px',
                                    bgcolor: COLORS.primary, borderRadius: '50%', border: '1px solid #F1F1F1'
                                }} />
                            )}
                        </NavIconItem>
                    </Tooltip>
                    {/* <NavIconItem>
                        <Box component="img" src={manage_clear_provider} sx={{ width: '18px', height: '18px' }} />
                    </NavIconItem>
                    <NavIconItem>
                        <Box component="img" src={views} sx={{ width: '18px', height: '18px' }} />
                    </NavIconItem>

                    <Box mt={2} mb={1} width="20px" height="1px" bgcolor="#D0D5DD" />

                    <NavIconItem>
                        <Box component="img" src={manage_provider_add} sx={{ width: '16px', height: '16px' }} />
                    </NavIconItem>
                    <NavIconItem>
                        <Box component="img" src={manage_delete_provider} sx={{ width: '12px', height: '12px' }} />
                    </NavIconItem> */}
                </NavSidebar>

                <SidebarContainer open={sidebarOpen}>
                    {activeView === 'filter' && (
                        <>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                                <Typography sx={{ fontSize: '18px', fontWeight: 600, color: COLORS.textDark }}>Filter</Typography>
                                <IconButton
                                    onClick={() => setActiveView(null)}
                                    size="small"
                                    sx={{ bgcolor: '#E5E7EB', '&:hover': { bgcolor: '#D1D5DB' } }}
                                >
                                    <LeftIcon sx={{ color: '#666', fontSize: '20px' }} />
                                </IconButton>
                            </Box>
                            <Divider sx={{ mb: 2, mx: -2 }} />
                            <FilterLabel>Search Provider</FilterLabel>
                            <StyledInput
                                fullWidth
                                placeholder="Search by npi"
                                variant="outlined"
                                size="small"
                                value={searchByNpi}
                                onChange={handleNpiChange}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <SearchIcon sx={{ color: '#ccc', fontSize: '15px' }} />
                                        </InputAdornment>
                                    )
                                }}
                            />
                            <FilterLabel>Select Section</FilterLabel>
                            <StyledSelect
                                fullWidth
                                size="small"
                                value={selectedSection}
                                onChange={(e) => {
                                    setSelectedSection(e.target.value);
                                    setSelectedField('');
                                    setFilterSearchValue('');
                                }}
                            >
                                <MenuItem value="">Select Section</MenuItem>
                                {metaData.map(section => (
                                    <MenuItem key={section.section_name} value={section.section_name}>
                                        {section.display_name}
                                    </MenuItem>
                                ))}
                            </StyledSelect>

                            {selectedSection && (
                                <>
                                    <FilterLabel>Select Field</FilterLabel>
                                    <StyledSelect
                                        fullWidth
                                        size="small"
                                        value={selectedField}
                                        onChange={(e) => setSelectedField(e.target.value)}
                                    >
                                        <MenuItem value="">Select Field</MenuItem>
                                        {(metaData.find(s => s.section_name === selectedSection)?.fields || []).map(field => (
                                            <MenuItem key={field.entity} value={field.entity}>
                                                {field.display_name}
                                            </MenuItem>
                                        ))}
                                    </StyledSelect>
                                </>
                            )}

                            {selectedField && (
                                <>
                                    <FilterLabel>Search Value</FilterLabel>
                                    <StyledInput
                                        fullWidth
                                        placeholder="Search Value"
                                        variant="outlined"
                                        size="small"
                                        value={filterSearchValue}
                                        onChange={(e) => setFilterSearchValue(e.target.value)}
                                        InputProps={{ endAdornment: (<InputAdornment position="end"><SearchIcon sx={{ color: '#ccc', fontSize: '15px' }} /></InputAdornment>) }}
                                    />
                                </>
                            )}

                            <FilterLabel>Date</FilterLabel>
                            <RadioGroup sx={{ ml: 1 }} value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}>
                                {['Last 6 Months', 'Last 3 Months', 'Last Month', 'Last Week'].map(label => (
                                    <FormControlLabel
                                        key={label} value={label.toLowerCase().replace(/ /g, '')}
                                        control={<Radio size="small" sx={{ color: '#E5E7EB', padding: '6px', '&.Mui-checked': { color: COLORS.primary } }} />}
                                        label={<Typography sx={{ fontSize: '13px', color: '#666' }}>{label}</Typography>}
                                    />
                                ))}
                                <FormControlLabel
                                    value="custom"
                                    control={<Radio size="small" sx={{ color: '#E5E7EB', padding: '6px', '&.Mui-checked': { color: COLORS.primary } }} />}
                                    label={<Typography sx={{ fontSize: '13px', color: '#666' }}>Custom Date</Typography>}
                                />
                            </RadioGroup>

                            {dateFilter === 'custom' && (
                                <Box mt={1} ml={2}>
                                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                                        <Typography sx={{ fontSize: '11px', width: '30px', color: COLORS.textGrey }}>From</Typography>
                                        <DatePicker
                                            value={fromDate} onChange={setFromDate}
                                            format="MM/dd/yyyy"
                                            slots={{ openPickerIcon: CalendarIcon }}
                                            slotProps={{
                                                textField: {
                                                    size: 'small',
                                                    fullWidth: true,
                                                    placeholder: "MM/DD/YYYY",
                                                    sx: { bgcolor: '#fff', '& .MuiOutlinedInput-root': { fontSize: '12px' } }
                                                },
                                                openPickerIcon: { sx: { color: '#ccc', fontSize: '12px' } }
                                            }}
                                        />
                                    </Box>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Typography sx={{ fontSize: '11px', width: '30px', color: COLORS.textGrey }}>To</Typography>
                                        <DatePicker
                                            value={toDate} onChange={setToDate}
                                            format="MM/dd/yyyy"
                                            slots={{ openPickerIcon: CalendarIcon }}
                                            slotProps={{
                                                textField: {
                                                    size: 'small',
                                                    fullWidth: true,
                                                    placeholder: "MM/DD/YYYY",
                                                    sx: { bgcolor: '#fff', '& .MuiOutlinedInput-root': { fontSize: '12px' } }
                                                },
                                                openPickerIcon: { sx: { color: '#ccc', fontSize: '12px' } }
                                            }}
                                        />
                                    </Box>
                                </Box>
                            )}

                            <Box mt={4} display="flex" gap={2}>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    sx={{
                                        textTransform: 'none',
                                        color: '#666',
                                        borderColor: '#E5E7EB',
                                        fontSize: '14px',
                                        height: '40px',
                                        borderRadius: '8px',
                                        bgcolor: '#fff',
                                        '&:hover': { bgcolor: '#F9FAFB', borderColor: '#D1D5DB' }
                                    }}
                                    onClick={handleResetFilters}
                                >
                                    Reset
                                </Button>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    sx={{ textTransform: 'none', bgcolor: COLORS.primary, fontSize: '12px', height: '36px', '&:hover': { bgcolor: '#E66F06' } }}
                                    onClick={handleViewAudits}
                                    disabled={loading}
                                >
                                    {loading ? 'Loading...' : 'View Audits'}
                                </Button>
                            </Box>
                        </>
                    )}
                    {activeView === 'health-plan' && (
                        <>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography sx={{ fontWeight: "500", color: COLORS.textDark }}>Select Health Plan</Typography>
                                <Button onClick={() => setActiveView(null)} sx={{ minWidth: 0, padding: 0 }}>
                                    <Box component="img" src={require('../../assets/icons/collapse.svg').default} sx={{ height: '22px' }} />
                                </Button>
                            </Box>
                            <HealthPlanSelector
                                onClose={() => setActiveView(null)}
                                healthPlans={healthPlans}
                                onApply={handleHealthPlanApply}
                                initialSelectedIds={selectedHealthPlanIds}
                            />
                        </>
                    )}
                </SidebarContainer>

                <MainContentArea>
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 0.5, gap: 4 }}>
                        <Box>
                            <SectionHeader sx={{ fontSize: '18px', fontWeight: 600 }}>Provider Change History</SectionHeader>
                        </Box>
                        <StyledInput
                            placeholder="Search by name"
                            variant="outlined"
                            size="small"
                            sx={{ width: '280px' }}
                            value={searchProvider}
                            onChange={(e) => {
                                setSearchProvider(e.target.value);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    if (gridApi) gridApi.refreshServerSide({ purge: true });
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#999', fontSize: '18px' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchProvider && (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setSearchProvider('');
                                                filtersRef.current.searchProvider = '';
                                                if (gridApi) gridApi.refreshServerSide({ purge: true });
                                            }}
                                            sx={{ color: '#999', p: 0.5 }}
                                        >
                                            <CloseIcon sx={{ fontSize: '14px' }} />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Box>

                    <ContentCard sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
                        <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee' }}>
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>
                                Search Results ({totalRecords})
                            </Typography>
                            <Button startIcon={<DownloadIcon sx={{ fontSize: '11px', fontWeight: 400 }} />} sx={{ textTransform: 'none', color: '#666666', fontSize: '11px', fontWeight: 400 }}>Download</Button>
                        </Box>
                        <Box className="root-provider-data" sx={{ flex: 1, width: '100%', overflow: 'hidden', padding: '10px 20px 20px 20px', boxSizing: 'border-box' }}>
                            <div className="ag-theme-manage-providers" style={{ height: 'calc(100vh - 180px)', width: '100%' }}>
                                <AgGridReact
                                    theme={"legacy"}
                                    onGridReady={onGridReady}
                                    onModelUpdated={onModelUpdated}
                                    onGridSizeChanged={onGridSizeChanged}
                                    quickFilterText={searchProvider}
                                    columnDefs={columnDefs}
                                    defaultColDef={defaultColDef}
                                    rowModelType="serverSide"
                                    pagination={true}
                                    paginationPageSize={20}
                                    cacheBlockSize={20}
                                    suppressRowHoverHighlight={true}
                                    masterDetail={true}
                                    detailCellRenderer={DetailCellRenderer}
                                    detailRowAutoHeight={true}
                                    rowHeight={60}
                                    headerHeight={40}
                                    suppressContextMenu={true}
                                    suppressDragLeaveHidesColumns={true}
                                    overlayNoRowsTemplate={`<div style='display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; color: ${errorMessage ? '#d32f2f' : '#666'};'>
                                        <div style='font-size: 14px; font-weight: 500;'>${errorMessage || 'No providers found'}</div>
                                    </div>`}
                                />
                            </div>
                        </Box>
                    </ContentCard>
                </MainContentArea>
            </PageWrapper>
        </LocalizationProvider >
    );
};

export default AuditTrails;